import React, { useState, useEffect, useRef } from 'react';
import { TextBoxComponent, NumericTextBoxComponent } from '@syncfusion/ej2-react-inputs';
import { DropDownListComponent } from '@syncfusion/ej2-react-dropdowns';
import { GridComponent, ColumnsDirective, ColumnDirective, Sort, Inject } from '@syncfusion/ej2-react-grids';
import { CrudPage, type CrudFormProps } from '../../components/CrudPage';
import { ReactTabs } from '../../components/ReactTabs';
import { DialogComponent } from '@syncfusion/ej2-react-popups';
import { ButtonComponent } from '@syncfusion/ej2-react-buttons';
import { CheckBoxComponent } from '@syncfusion/ej2-react-buttons';
import { type OperationDto } from '../../api/models/OperationDto';
import { type OperationInputQuantityDto } from '../../api/models/OperationInputQuantityDto';
import { type OperationModeDto } from '../../api/models/OperationModeDto';
import { type MaterialDto } from '../../api/models/MaterialDto';
import { type ResourceTypeDto } from '../../api/models/ResourceTypeDto';
import { type ResourceDto } from '../../api/models/ResourceDto';
import { MaterialControllerService } from '../../api/services/MaterialControllerService';
import { ResourceTypeControllerService } from '../../api/services/ResourceTypeControllerService';
import { ResourceControllerService } from '../../api/services/ResourceControllerService';
import { useTranslation } from 'react-i18next';
import { useDropdownFormInitializer } from '../../hooks/useDropdownFormInitializer';
import { useToast } from '../../hooks/useToast';
import { DurationInput } from '../../components/DurationInput';
import { generateTempUUID, isTempUUID } from '../../utils/uuid';

type OperationWithId = OperationDto & { 
  id: string;
  durationSeconds?: number;
  durationPerPieceSeconds?: number;
  parallelProcessing?: boolean;
};

interface OperationDetailDialogProps extends CrudFormProps<OperationWithId> {
  routingOutputMaterialId?: string;
}


const OperationDetailDialog: React.FC<OperationDetailDialogProps> = ({
  value,
  onClose,
  onSave,
  routingOutputMaterialId
}) => {
  const { t } = useTranslation(['common']);
  const { showToast } = useToast();

  // Basic operation fields
  const [name, setName] = useState(value?.name || '');
  const [description, setDescription] = useState(value?.description || '');
  
  // Validation state
  const [validationErrors, setValidationErrors] = useState<{[key: string]: boolean}>({});
  const [, setHasInteracted] = useState<{[key: string]: boolean}>({});
  const [showValidationErrors, setShowValidationErrors] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState(false);
  
  // Input quantities management
  const [inputQuantities, setInputQuantities] = useState<(OperationInputQuantityDto & { index: number })[]>([]);
  const [materials, setMaterials] = useState<MaterialDto[]>([]);
  // Keep finished products separate: the output material of a routing is typically a PRODUCT
  // and therefore drops out of the input filter (INTERMEDIATE_PRODUCT + RAW_MATERIAL). Without
  // its own list the longRunning lookup for the long-running toggle fails.
  const [outputProducts, setOutputProducts] = useState<MaterialDto[]>([]);
  const [selectedTabIndex, setSelectedTabIndex] = useState<number>(0);
  /** Bumped so that the embedded CrudPage re-reads its data when inputQuantities changes. */
  const [inputQuantitiesRefreshKey, setInputQuantitiesRefreshKey] = useState(0);
  /**
   * Ref holding the current inputQuantities. Syncfusion's footerTemplate can capture the
   * handleSave closure from the first render - without this ref, entries added through the
   * sub-dialog after the first render would be missing on OK.
   */
  const inputQuantitiesRef = useRef<(OperationInputQuantityDto & { index: number })[]>([]);
  useEffect(() => {
    inputQuantitiesRef.current = inputQuantities;
  }, [inputQuantities]);
  
  // Execution modes management - direct form fields
  const [resourceTypes, setResourceTypes] = useState<ResourceTypeDto[]>([]);
  const [resources, setResources] = useState<ResourceDto[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Execution mode form fields
  const [primaryResourceTypeId, setPrimaryResourceTypeId] = useState<string>('');
  const [timeType, setTimeType] = useState<string>('');
  const [globalDuration, setGlobalDuration] = useState<number | null>(null);
  const [durationPerPiece, setDurationPerPiece] = useState<number | null>(null);
  const [capacityDemandType, setCapacityDemandType] = useState<string>('PER_BATCH');
  const capacityDemandTypeRef = useRef<string>('PER_BATCH');
  
  // Update ref when capacityDemandType changes
  useEffect(() => {
    capacityDemandTypeRef.current = capacityDemandType;
  }, [capacityDemandType]);
  const [parallelProcessing, setParallelProcessing] = useState<boolean>(false);
  const [longTermManaged, setLongTermManaged] = useState<boolean>(false);
  const [outputMaterialLongRunning, setOutputMaterialLongRunning] = useState<boolean>(false);
  const [, setSelectedResourceType] = useState<ResourceTypeDto | null>(null);
  
  // Distance to predecessor fields
  const [minDistancePredecessor, setMinDistancePredecessor] = useState<number | null>(null);
  const [maxDistancePredecessor, setMaxDistancePredecessor] = useState<number | null>(null);
  
  // Attribute values for setup attributes
  const [setupAttributeValues, setSetupAttributeValues] = useState<{[attributeId: string]: string | number | boolean}>({});
  
  // Refs for DurationInput components to commit values
  const globalDurationRef = useRef<{ commit: () => void } | null>(null);
  const durationPerPieceRef = useRef<{ commit: () => void } | null>(null);
  const minDistancePredecessorRef = useRef<{ commit: () => void } | null>(null);
  const maxDistancePredecessorRef = useRef<{ commit: () => void } | null>(null);
  const resourcesGridRef = useRef<GridComponent | null>(null);

  // Load data function for the hook
  const loadData = async () => {
    try {
      const [materialsData, resourceTypesData, resourcesData] = await Promise.all([
        MaterialControllerService.findAll(),
        ResourceTypeControllerService.findAll(),
        ResourceControllerService.findAll()
      ]);

      // Input materials are intermediate products and raw materials only. Finished products are
      // excluded because they cannot be the input of another production step.
      const eligibleMaterials = materialsData.filter(m =>
        m.materialType === 'INTERMEDIATE_PRODUCT' || m.materialType === 'RAW_MATERIAL'
      );
      // The output of a finished-product routing is always a PRODUCT - kept separate for resolving
      // the longRunning flag (long-running checkbox).
      const outputProductMaterials = materialsData.filter(m => m.materialType === 'PRODUCT');

      return {
        materials: eligibleMaterials,
        outputProducts: outputProductMaterials,
        resourceTypes: resourceTypesData,
        resources: resourcesData
      };
    } catch (error) {
      console.error('Error loading data:', error);
      return { materials: [], outputProducts: [], resourceTypes: [], resources: [] };
    }
  };

  // Set state functions for the hook
  const setStateFunctions = {
    materials: setMaterials,
    outputProducts: setOutputProducts,
    resourceTypes: setResourceTypes,
    resources: setResources
  };

  // Use the custom hook for proper timing
  useDropdownFormInitializer(
    true, // Always open when component mounts
    loadData,
    setStateFunctions,
    () => {
      setLoading(false);
    },
    {}
  );

  // Output material long-running: only show the long-running option when the routing output is long-running
  useEffect(() => {
    if (!routingOutputMaterialId || outputProducts.length === 0) {
      setOutputMaterialLongRunning(false);
      return;
    }
    const outputMaterial = outputProducts.find(m => m.uuid === routingOutputMaterialId);
    setOutputMaterialLongRunning(outputMaterial?.longRunning === true);
  }, [routingOutputMaterialId, outputProducts]);

  // Time type options - filtered based on parallel processing and resource type
  // Constraint:
  // - parallelProcessing = true → only QUANTITY_DEPENDENT and QUANTITY_AND_RESOURCE_DEPENDENT
  const getTimeTypeOptions = React.useCallback(() => {
    const allOptions = [
      { text: t('routing.form.timeTypeOptions.fixedDuration'), value: 'FIXED_DURATION' },
      { text: t('routing.form.timeTypeOptions.quantityDependent'), value: 'QUANTITY_DEPENDENT' },
      { text: t('routing.form.timeTypeOptions.resourceDependent'), value: 'RESOURCE_DEPENDENT' },
      { text: t('routing.form.timeTypeOptions.quantityAndResourceDependent'), value: 'QUANTITY_AND_RESOURCE_DEPENDENT' }
    ];

    // Get current resource type
    const currentResourceType = resourceTypes.find(rt => rt.uuid === primaryResourceTypeId);

    // Constraint: parallelProcessing = true → only QUANTITY_DEPENDENT and QUANTITY_AND_RESOURCE_DEPENDENT
    // Note: parallelProcessing can only be true if singleResource = false
    if (parallelProcessing && currentResourceType?.singleResource !== true) {
      return allOptions.filter(option => 
        option.value === 'QUANTITY_DEPENDENT' || option.value === 'QUANTITY_AND_RESOURCE_DEPENDENT'
      );
    }

    // Otherwise, show all options
    return allOptions;
  }, [parallelProcessing, primaryResourceTypeId, resourceTypes, t]);

  // Make timeTypeOptions reactive to parallelProcessing and resource type changes
  const timeTypeOptions = React.useMemo(() => getTimeTypeOptions(), [getTimeTypeOptions]);

  // Resource type options with status indicators
  const resourceTypeOptions = resourceTypes
    .filter(rt => rt.name && rt.uuid)
    .map(rt => {
      let icon = '';
      
      
      if (rt.singleResource) {
        icon = ` 👤`;
      } else {
        icon = ` ⚙️`;
      }
      
      return {
        text: `${icon} ${rt.name!}`,
        value: rt.uuid!
      };
    });

  // Unit types for input quantities
  const unitTypes = [
    { text: t('form.unitType.piece'), value: 'PIECE' },
    { text: t('form.unitType.mass'), value: 'MASS' },
    { text: t('form.unitType.volume'), value: 'VOLUME' },
    { text: t('form.unitType.length'), value: 'LENGTH' },
    { text: t('form.unitType.area'), value: 'AREA' }
  ];

  // Initialize form fields when value changes (for editing or adding)
  useEffect(() => {
    if (!loading) {
      // Reset form when value is null (new operation)
      if (!value) {
        setName('');
        setDescription('');
        setInputQuantities([]);
        setPrimaryResourceTypeId('');
        setTimeType('');
        setGlobalDuration(null);
        setDurationPerPiece(null);
        setCapacityDemandType('PER_BATCH');
        setParallelProcessing(false);
        setLongTermManaged(false);
        setSetupAttributeValues({});
        return;
      }
      
      // Initialize from value (existing or new operation)
      try {
        setName(value?.name || '');
        setDescription(value?.description || '');
        
        // Defensive check: Ensure inputQuantities is an array
        const safeInputQuantities = Array.isArray(value?.inputQuantities) 
          ? value.inputQuantities 
          : [];
        setInputQuantities(safeInputQuantities.map((item, index) => ({ ...item, index })));
        
        // Initialize execution mode fields directly from OperationDto
        // The fields are now directly on OperationDto, not in operationModes
        if (value?.primaryResourceTypeId) {
          setPrimaryResourceTypeId(value.primaryResourceTypeId);
        }
        
        if (value?.timeType) {
          setTimeType(value.timeType);
        } else {
          setTimeType(''); // Empty for new operations
        }
        
        // Initialize attribute values if they exist (now directly on OperationDto)
        // Defensive check: Ensure setupAttributeValues is an array
        if (Array.isArray(value?.setupAttributeValues) && value.setupAttributeValues.length > 0) {
          const setupValues: {[key: string]: any} = {};
          value.setupAttributeValues.forEach(attr => {
            if (attr && attr.attributeDefinitionId) {
              setupValues[attr.attributeDefinitionId] = attr.value;
            }
          });
          setSetupAttributeValues(setupValues);
        }
        
        // For FIXED_DURATION and QUANTITY_DEPENDENT, use direct OperationDto fields
        // Important: Check for !== undefined && !== null, not truthy, because 0 is a valid value
        if (value?.timeType === 'FIXED_DURATION' && value.durationSeconds !== undefined && value.durationSeconds !== null) {
          setGlobalDuration(value.durationSeconds);
        } else if (value?.timeType === 'QUANTITY_DEPENDENT' && value.durationPerPieceSeconds !== undefined && value.durationPerPieceSeconds !== null) {
          setDurationPerPiece(value.durationPerPieceSeconds);
        } else if (value?.timeType === 'RESOURCE_DEPENDENT' || value?.timeType === 'QUANTITY_AND_RESOURCE_DEPENDENT') {
          // For RESOURCE_DEPENDENT and QUANTITY_AND_RESOURCE_DEPENDENT, use operation mode durations
          // Important: Check for !== undefined && !== null, not truthy, because 0 is a valid value
          // Defensive check: Ensure operationModes is an array before accessing [0]
          const safeOperationModes = Array.isArray(value?.operationModes) ? value.operationModes : [];
          const existingMode = safeOperationModes[0];
          if (existingMode) {
            if (value.timeType === 'RESOURCE_DEPENDENT' && existingMode.durationSeconds !== undefined && existingMode.durationSeconds !== null) {
              setGlobalDuration(existingMode.durationSeconds);
            } else if (value.timeType === 'QUANTITY_AND_RESOURCE_DEPENDENT' && existingMode.durationPerPieceSeconds !== undefined && existingMode.durationPerPieceSeconds !== null) {
              setDurationPerPiece(existingMode.durationPerPieceSeconds);
            }
          }
        }
      } catch (error) {
        console.error('Error initializing form fields:', error, { value });
        // Don't throw - just log and continue with defaults
      }
      
      if (value?.longTermManaged !== undefined) {
        setLongTermManaged(value.longTermManaged);
      }
      // Initialize parallelProcessing if it exists
      // IMPORTANT: Validate against ResourceType - parallelProcessing can only be true if singleResource = false
      if (value?.parallelProcessing !== undefined) {
        // Get resource type to validate parallelProcessing
        const resourceType = resourceTypes.find(rt => rt.uuid === value.primaryResourceTypeId);
        if (resourceType?.singleResource === true && value.parallelProcessing === true) {
          // Constraint violation: singleResource=true but parallelProcessing=true
          console.warn('Invalid data from backend: singleResource=true but parallelProcessing=true. Setting parallelProcessing to false.');
          setParallelProcessing(false);
        } else {
          setParallelProcessing(value.parallelProcessing);
        }
      }
      
      // Initialize capacityDemandType if it exists, otherwise default to PER_BATCH
      setCapacityDemandType(value?.capacityDemandType || 'PER_BATCH');
      
      // Initialize distance to predecessor fields
      // Important: 0 is a valid value, so we must check explicitly for undefined/null
      if (value?.minDistancePredecessor !== undefined && value.minDistancePredecessor !== null) {
        setMinDistancePredecessor(value.minDistancePredecessor);
      } else {
        setMinDistancePredecessor(null);
      }
      if (value?.maxDistancePredecessor !== undefined && value.maxDistancePredecessor !== null) {
        setMaxDistancePredecessor(value.maxDistancePredecessor);
      } else {
        setMaxDistancePredecessor(null);
      }
      
      
    }
  }, [value, loading, resourceTypes]);

  // Update selected resources when primary resource type changes
  useEffect(() => {
    if (primaryResourceTypeId && resources.length > 0) {
      try {
        const resourceType = resourceTypes.find(rt => rt.uuid === primaryResourceTypeId);
        setSelectedResourceType(resourceType || null);
        
        setCapacityDemandType('PER_BATCH');
        
        // Reset attribute values when resource type changes
        setSetupAttributeValues({});
        
        // Check if current time type is allowed for parallel processing state
        const allowedTimeTypes = getTimeTypeOptions();
        // Only reset if timeType is set but not in allowed types
        // Don't auto-select if timeType is empty (user hasn't selected yet)
        if (timeType && !allowedTimeTypes.some(option => option.value === timeType)) {
          // Reset to first allowed time type, or empty if no types available
          setTimeType(allowedTimeTypes[0]?.value || '');
        }
        
        // If editing, preserve existing selections and durations from all operationModes
        // CRITICAL: Defensive check - ensure operationModes is an array before processing
        const safeOperationModes = Array.isArray(value?.operationModes) 
          ? value.operationModes 
          : [];
        
        // Create a map of resourceId -> operationMode for quick lookup
        // operationMode.resourceId contains the UUID of the resource
        const operationModesMap = new Map<string, any>();
        
        // Only iterate if we have a valid array
        safeOperationModes.forEach(mode => {
          // Additional safety check: ensure mode is an object with resourceId
          if (mode && typeof mode === 'object' && mode.resourceId) {
            // Store with resourceId as key (should be UUID string)
            operationModesMap.set(String(mode.resourceId), mode);
          }
        });
        
        // Note: gridRows will be initialized by gridRowsMemo, which uses the same logic
        // No need to set selectedResources anymore - we only use gridRows
      } catch (error) {
        console.error('Error updating selected resources:', error, {
          primaryResourceTypeId,
          resourcesLength: resources.length,
          operationModes: value?.operationModes,
          operationModesType: typeof value?.operationModes,
          isArray: Array.isArray(value?.operationModes)
        });
        // Fallback: gridRows will be cleared by gridRowsMemo when primaryResourceTypeId is cleared
      }
    }
  }, [primaryResourceTypeId, resources, resourceTypes, value, getTimeTypeOptions, timeType]);

  // Calculate currentSelectedResourceType in a stable memo (must be before useEffect hooks that use it)
  const currentSelectedResourceType = React.useMemo(() => {
    return resourceTypes.find(rt => rt.uuid === primaryResourceTypeId) || null;
  }, [resourceTypes, primaryResourceTypeId]);

  // Constraint: parallelProcessing can only be true if singleResource = false
  // If singleResource = true, automatically set parallelProcessing to false
  // This useEffect ensures the constraint is enforced whenever resourceType or parallelProcessing changes
  // It also handles the case where resourceTypes are loaded after parallelProcessing is initialized
  useEffect(() => {
    if (currentSelectedResourceType?.singleResource === true && parallelProcessing) {
      setParallelProcessing(false);
    }
  }, [currentSelectedResourceType?.singleResource, parallelProcessing, currentSelectedResourceType]);
  
  // Additional validation: If resourceTypes are loaded after parallelProcessing is initialized,
  // validate and correct parallelProcessing based on the resource type
  useEffect(() => {
    if (resourceTypes.length > 0 && primaryResourceTypeId && parallelProcessing) {
      const resourceType = resourceTypes.find(rt => rt.uuid === primaryResourceTypeId);
      if (resourceType?.singleResource === true) {
        setParallelProcessing(false);
      }
    }
  }, [resourceTypes, primaryResourceTypeId, parallelProcessing]);

  // Constraint: When long-term managed is active, fix timeType to FIXED_DURATION and disable parallel processing
  useEffect(() => {
    if (outputMaterialLongRunning && longTermManaged) {
      setParallelProcessing(false);
      setTimeType('FIXED_DURATION');
    }
  }, [outputMaterialLongRunning, longTermManaged]);

  // Central function to calculate column visibility based on all dependencies
  interface ColumnVisibility {
    showDuration: boolean;
    showCapacityDemand: boolean;
    showMinMaxCapacityDemand: boolean;
    capacityDemandFormat: 'DECIMAL' | 'NONE';
  }

  const calculateColumnVisibility = React.useCallback((
    resourceType: ResourceTypeDto | null,
    parallelProcessingValue: boolean,
    timeTypeValue: string
  ): ColumnVisibility => {
    
    // Default: all columns hidden
    let showDuration = false;
    let showCapacityDemand = false;
    let showMinMaxCapacityDemand = false;
    let capacityDemandFormat: 'DECIMAL' | 'NONE' = 'NONE';

    // Early return if no resource type
    if (!resourceType || !primaryResourceTypeId) {
      return {
        showDuration,
        showCapacityDemand,
        showMinMaxCapacityDemand,
        capacityDemandFormat
      };
    }

    const isSingleResource = resourceType.singleResource === true;
    
    // Constraint: parallelProcessing can only be true if singleResource = false
    const effectiveParallelProcessing = !isSingleResource && parallelProcessingValue;
    
    // Duration column: only visible for resource-dependent time types
    const isResourceDependent = timeTypeValue === 'RESOURCE_DEPENDENT' || 
                                timeTypeValue === 'QUANTITY_AND_RESOURCE_DEPENDENT';
    
    
    // Only show additional columns if time type is resource-dependent
    if (isResourceDependent) {
      // Duration is always shown for resource-dependent time types
      showDuration = true;
      
      // Capacity Demand visibility logic (linear, no nested returns)
      // Rule: Show if NOT parallel processing AND NOT single resource
      if (!effectiveParallelProcessing) {
        if (!isSingleResource) {
          // Normal resource (multi-resource): show capacity demand as DECIMAL
          showCapacityDemand = true;
          capacityDemandFormat = 'DECIMAL';
        }
        // else: singleResource=true -> no capacity demand (already false)
      }
      // else: effectiveParallelProcessing=true -> show Min/Max instead (see below)
      
      // Min/Max Capacity Demand: only for parallel processing with normal resources
      if (effectiveParallelProcessing) {
        showMinMaxCapacityDemand = true;
        capacityDemandFormat = 'NONE'; // Capacity demand is hidden when Min/Max is shown
      }
    }
    // else: FIXED_DURATION or QUANTITY_DEPENDENT -> all columns stay false (values set at operation level)

    return {
      showDuration,
      showCapacityDemand,
      showMinMaxCapacityDemand,
      capacityDemandFormat
    };
  }, [primaryResourceTypeId]);

  // Calculate column visibility using memoized function
  // IMPORTANT: timeType must be a string, not empty string or undefined
  const columnVisibility = React.useMemo(() => {
    // Ensure timeType is a valid string before calculating
    const validTimeType = timeType && typeof timeType === 'string' && timeType.length > 0 ? timeType : '';
    const result = calculateColumnVisibility(currentSelectedResourceType, parallelProcessing, validTimeType);
    return result;
  }, [calculateColumnVisibility, currentSelectedResourceType, parallelProcessing, timeType]);
  

  // Use the same pattern as SelectableListComponent: memo for structural data, state for display
  // This prevents grid refresh when only selection changes
  const gridRowsMemo = React.useMemo(() => {
    if (!primaryResourceTypeId || resources.length === 0) {
      return [];
    }
    
    try {
      // Filter resources by resource type - show ALL resources of this type
      const typeResources = resources.filter(r => r.resourceTypeUuid === primaryResourceTypeId);
      
      // CRITICAL: Defensive check - ensure operationModes is an array
      const safeOperationModes = Array.isArray(value?.operationModes) 
        ? value.operationModes 
        : [];
      
      // Create a map of resourceId -> operationMode for quick lookup
      const operationModesMap = new Map<string, any>();
      safeOperationModes.forEach(mode => {
        if (mode && typeof mode === 'object' && mode.resourceId) {
          operationModesMap.set(String(mode.resourceId), mode);
        }
      });
      
      // Check if singleResource (capacity is always 1.0)
      const currentResourceType = resourceTypes.find(rt => rt.uuid === primaryResourceTypeId);
      const isSingleResource = currentResourceType?.singleResource === true;
      
      const rows = typeResources.map(resource => {
        const resourceIdentifier = resource?.uuid || (resource as any)?.id;
        let existingMode: any = undefined;
        
        if (resourceIdentifier) {
          existingMode = operationModesMap.get(String(resourceIdentifier));
          if (!existingMode) {
            existingMode = safeOperationModes.find(mode => {
              if (!mode || !mode.resourceId) return false;
              return String(mode.resourceId) === String(resourceIdentifier);
            });
          }
        }
        
        // Calculate capacityDemand
        let capacityDemandValue: number | undefined = undefined;
        if (isSingleResource) {
          capacityDemandValue = 1.0;
        } else if (existingMode?.capacityDemand !== undefined && existingMode.capacityDemand !== null) {
          capacityDemandValue = existingMode.capacityDemand;
        }
        
        // Determine selected state from existing mode (for Grid initialization only)
        // NOTE: 'selected' is NOT stored in gridRows state - it's only used for initializing Grid selection
        const isSelected = !!existingMode;
        
        // Calculate duration values
        const durationValue = existingMode?.durationSeconds !== undefined && existingMode?.durationSeconds !== null
          ? existingMode.durationSeconds
          : null;
        const durationPerPieceValue = existingMode?.durationPerPieceSeconds !== undefined && existingMode?.durationPerPieceSeconds !== null
          ? existingMode.durationPerPieceSeconds
          : null;
        
        return {
          resource,
          // Store selected temporarily for Grid initialization (will be removed from state)
          _initialSelected: isSelected,
          duration: durationValue ?? 0,
          durationPerPiece: durationPerPieceValue ?? 0,
          capacityDemand: capacityDemandValue,
          minCapacityDemand: existingMode?.minCapacityDemand ?? 0,
          maxCapacityDemand: existingMode?.maxCapacityDemand ?? 0,
          operationModeUuid: existingMode?.uuid
        };
      });
      
      return rows;
    } catch (error) {
      console.error('🔍 gridRowsMemo: Error calculating gridRows:', error);
      return [];
    }
  }, [primaryResourceTypeId, resources, value?.operationModes, resourceTypes]); // NO timeType, NO selectedResources - only structural data
  
  // Log when gridRowsMemo changes
  React.useEffect(() => {
  }, [gridRowsMemo, primaryResourceTypeId, resources.length, value?.operationModes?.length]);
  
  // State for display - synchronized with gridRowsMemo, but only updates when data actually changes
  // NOTE: 'selected' is NOT stored in state - it's only managed by the Grid component itself
  // This prevents re-renders when selection changes
  const [gridRows, setGridRows] = React.useState<Array<{
    resource: ResourceDto;
    duration: number;
    durationPerPiece: number;
    capacityDemand?: number;
    minCapacityDemand: number;
    maxCapacityDemand: number;
    operationModeUuid?: string;
  }>>([]);
  
  // Ref to store current gridRows for dialogContent (prevents re-creation on every render)
  // Must be declared AFTER gridRows state
  // CRITICAL: Update ref synchronously when gridRows changes to ensure dialogContent has latest data
  const gridRowsRefForContent = React.useRef(gridRows);
  // Update ref immediately when gridRows changes (before useEffect)
  gridRowsRefForContent.current = gridRows;
  
  // Also update ref in useEffect to ensure it's always in sync
  React.useEffect(() => {
    gridRowsRefForContent.current = gridRows;
  }, [gridRows]);
  
  // Synchronize gridRowsMemo with gridRows (like SelectableListComponent pattern)
  // NOTE: 'selected' is NOT stored in state - it's only managed by the Grid component
  React.useEffect(() => {
    
    // CRITICAL: If gridRowsMemo has data but gridRows is empty, always update
    // This handles the case when editing an existing operation with operationModes
    if (gridRowsMemo.length > 0 && gridRows.length === 0) {
      // Remove _initialSelected from state (it's only for Grid initialization)
      const rowsWithoutSelected = gridRowsMemo.map(({ _initialSelected, ...row }) => row);
      setGridRows(rowsWithoutSelected);
      return;
    }
    
    // If gridRowsMemo is empty, clear gridRows
    if (gridRowsMemo.length === 0) {
      if (gridRows.length > 0) {
        setGridRows([]);
      }
      return;
    }
    
    const memoRows = gridRowsMemo;
    setGridRows(prev => {
      // Only update if data actually changed
      const hasChanged = prev.length !== memoRows.length ||
        prev.some((item, i) => {
          const memoItem = memoRows[i];
          if (!memoItem) {
            return true;
          }
          // Check if resource UUID changed
          if (item.resource.uuid !== memoItem.resource.uuid) {
            return true;
          }
          // Check other fields (selected is NOT in state anymore)
          if (item.duration !== memoItem.duration) {
            return true;
          }
          if (item.durationPerPiece !== memoItem.durationPerPiece) {
            return true;
          }
          if (item.capacityDemand !== memoItem.capacityDemand) {
            return true;
          }
          if (item.minCapacityDemand !== memoItem.minCapacityDemand) {
            return true;
          }
          if (item.maxCapacityDemand !== memoItem.maxCapacityDemand) {
            return true;
          }
          return false;
        });
      if (!hasChanged) {
        return prev; // Return same reference to prevent re-render
      }
      // Remove _initialSelected from state (it's only for Grid initialization)
      return memoRows.map(({ _initialSelected, ...row }) => row);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- gridRows.length intentionally excluded to avoid infinite loop (this effect sets gridRows)
  }, [gridRowsMemo]);
  
  // Initialize grid selection when gridRowsMemo changes (for when dialog opens with existing data)
  // Use gridRowsMemo._initialSelected to determine which rows should be selected
  // Use multiple attempts with increasing delays to ensure grid is fully rendered
  React.useEffect(() => {
    
    if (resourcesGridRef.current && gridRowsMemo.length > 0) {
      const selectedIndices: number[] = [];
      gridRowsMemo.forEach((row, index) => {
        // Use _initialSelected from gridRowsMemo (not from state)
        if ((row as any)._initialSelected) {
          selectedIndices.push(index);
        }
      });
      
      
      if (selectedIndices.length === 0) {
        return; // No rows to select
      }
      
      if (selectedIndices.length === 0) {
        return; // No rows to select
      }
      
      
      // Try multiple times with increasing delays to ensure grid is fully rendered
      const timeouts: NodeJS.Timeout[] = [];
      
      [50, 150, 300].forEach((delay, attempt) => {
        const timeoutId = setTimeout(() => {
          const grid = resourcesGridRef.current;
          if (!grid) {
            return;
          }
          
          try {
            // Get current selection to avoid unnecessary updates
            const currentSelected = grid.getSelectedRowIndexes();
            const needsUpdate = currentSelected.length !== selectedIndices.length ||
              !selectedIndices.every(idx => currentSelected.includes(idx));
            
            
            if (needsUpdate) {
              grid.clearSelection();
              grid.selectRows(selectedIndices);
              
              // Verify selection was applied
              setTimeout(() => {
                const verifySelected = grid.getSelectedRowIndexes();
                const isCorrect = verifySelected.length === selectedIndices.length &&
                  selectedIndices.every(idx => verifySelected.includes(idx));
                
                
                if (isCorrect) {
                  // Clear remaining timeouts
                  timeouts.forEach(t => clearTimeout(t));
                }
              }, 50);
            } else {
              // Clear remaining timeouts
              timeouts.forEach(t => clearTimeout(t));
            }
          } catch (error) {
            console.error(`🟡 Grid selection attempt ${attempt + 1}: Error`, error);
          }
        }, delay);
        
        timeouts.push(timeoutId);
      });
      
      return () => {
        timeouts.forEach(t => clearTimeout(t));
      };
    }
  }, [gridRowsMemo, primaryResourceTypeId]); // React to gridRowsMemo changes (for initial selection)

  // Create content function - use existing ref to always get the latest gridRows value
  // This ensures the Grid is visible immediately when gridRows is populated
  // Note: gridRowsRefForContent is already declared earlier and updated via useEffect
  
  const dialogContent = () => {
    // Read gridRows from ref to always get the latest value, even if React hasn't re-rendered yet
    const currentGridRows = gridRowsRefForContent.current;

    return (
    // Guard form: catches submit events from embedded grids / sub-dialogs so that they do not
    // propagate to RoutingFormDialog (the enclosing form) and trigger its onSubmit.
    <form onSubmit={(e) => { e.preventDefault(); e.stopPropagation(); }}>
    <div className="p-4" style={{ height: '100%', display: 'flex', flexDirection: 'column', overflowY: 'auto', overflowX: 'hidden' }}>
            <div className="space-y-6" style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {/* Basic Operation Information */}
            <div style={{ flexShrink: 0 }}>
              <h4 className="text-lg font-semibold mb-4">{t('routing.form.basicOperationInfo')}</h4>
              <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 items-end">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('form.name')} *
                  </label>
                  <TextBoxComponent
                    placeholder={t('form.name')}
                    value={name}
                    change={(e: any) => {
                      setName(e.value);
                      // Clear validation error when user starts typing
                      if (validationErrors.name) {
                        setValidationErrors(prev => ({ ...prev, name: false }));
                        setShowValidationErrors(false);
                      }
                    }}
                    cssClass={validationErrors.name ? 'e-error' : ''}
                  />
                  {showValidationErrors && validationErrors.name && (
                    <div className="text-red-500 text-sm mt-1">{t('form.name')} {t('form.required')}</div>
                  )}
                </div>
                {outputMaterialLongRunning && (
                  <div className="flex items-center pb-1">
                    <CheckBoxComponent
                      checked={longTermManaged}
                      change={(e: any) => setLongTermManaged(Boolean(e.checked))}
                    />
                    <label className="ml-2 text-sm font-medium text-gray-700 whitespace-nowrap">
                      {t('routing.form.longTermManaged')}
                    </label>
                  </div>
                )}
              </div>
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('form.description')}
                </label>
                <TextBoxComponent
                  placeholder={t('form.description')}
                  value={description}
                  change={(e: any) => setDescription(e.value)}
                />
              </div>
              
              {/* Distance to Predecessor Fields - Always shown, but disabled for first operation */}
              <div className="mt-4 grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('operation.minDistancePredecessor', 'Min. Abstand zum Vorgänger')}
                  </label>
                  <DurationInput
                    value={minDistancePredecessor !== null && minDistancePredecessor !== undefined ? minDistancePredecessor : undefined}
                    required={false}
                    disabled={value?.operationNumber === 1}
                    onRef={(ref) => {
                      minDistancePredecessorRef.current = ref;
                    }}
                    onChange={(value) => {
                      setMinDistancePredecessor(value);
                    }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('operation.maxDistancePredecessor', 'Max. Abstand zum Vorgänger')}
                  </label>
                  <DurationInput
                    value={maxDistancePredecessor !== null && maxDistancePredecessor !== undefined ? maxDistancePredecessor : undefined}
                    required={false}
                    disabled={value?.operationNumber === 1}
                    onRef={(ref) => {
                      maxDistancePredecessorRef.current = ref;
                    }}
                    onChange={(value) => {
                      setMaxDistancePredecessor(value);
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Tabbed Sections: Execution Modes and Input Quantities */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <ReactTabs
              selectedIndex={selectedTabIndex}
              onSelect={setSelectedTabIndex}
              style={{ flex: 1 }}
              items={[
                // Execution Modes Tab
                {
                  header: t('routing.form.executionModesTab'),
                  content: () => (
                    <div className="p-4" style={{ height: '100%', overflowY: 'auto' }}>
                      {/* Execution Mode Fields - Direct Form Fields */}
                      <div>
                        <div className="mb-4">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t('routing.form.primaryResourceType')} *
                          </label>
                          <DropDownListComponent
                            dataSource={resourceTypeOptions}
                            fields={{ text: 'text', value: 'value' }}
                            placeholder={t('routing.form.primaryResourceType')}
                            value={primaryResourceTypeId}
                            change={(e: any) => {
                              setPrimaryResourceTypeId(e.value);
                              // Clear timeType when primaryResourceTypeId changes (user must select again)
                              setTimeType('');
                              // Clear gridRows when primaryResourceTypeId changes
                              setGridRows([]);
                              // Clear validation error when user makes selection
                              if (validationErrors.primaryResourceType) {
                                setValidationErrors(prev => ({ ...prev, primaryResourceType: false }));
                                setShowValidationErrors(false);
                              }
                            }}
                            cssClass={validationErrors.primaryResourceType ? 'e-error' : ''}
                          />
                          {showValidationErrors && validationErrors.primaryResourceType && (
                            <div className="text-red-500 text-sm mt-1">{t('routing.form.primaryResourceType')} {t('form.required')}</div>
                          )}
                        </div>

                        {/* Setup Attributes - shown when primary resource type is selected and has setup attributes (excluding MATERIAL type) */}
                        {(() => {
                          const nonMaterialSetupAttributes = currentSelectedResourceType?.setupAttributes?.filter(attr => attr.valueType !== 'MATERIAL') || [];
                          return nonMaterialSetupAttributes.length > 0;
                        })() && (
                          <div className="mb-4">
                            <h6 className="text-sm font-semibold mb-2">{t('routing.form.setupAttributes', 'Rüstattribute')}</h6>
                            <div className="space-y-3 border rounded-lg p-3 bg-gray-50">
                              {currentSelectedResourceType?.setupAttributes
                                ?.filter(attr => attr.valueType !== 'MATERIAL')
                                .map((attr) => {
                                const attrId = attr.uuid || '';
                                const currentValue = setupAttributeValues[attrId];
                                
                                // Parse valueList if it exists (comma-separated)
                                const valueList = attr.valueList ? attr.valueList.split(',').map(v => v.trim()) : [];
                                
                                return (
                                  <div key={attrId}>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      {attr.name}
                                      {attr.description && (
                                        <span className="text-xs text-gray-500 ml-2">({attr.description})</span>
                                      )}
                                    </label>
                                    {valueList.length > 0 ? (
                                      // Dropdown for value list
                                      <DropDownListComponent
                                        dataSource={valueList.map(v => ({ text: v, value: v }))}
                                        fields={{ text: 'text', value: 'value' }}
                                        placeholder={t('form.select', 'Auswählen...')}
                                        value={currentValue as string || ''}
                                        change={(e: any) => {
                                          setSetupAttributeValues(prev => ({ ...prev, [attrId]: e.value || '' }));
                                        }}
                                      />
                                    ) : attr.valueType === 'NUMBER' ? (
                                      // Numeric input
                                      <NumericTextBoxComponent
                                        placeholder={attr.min !== undefined && attr.max !== undefined 
                                          ? `${attr.min} - ${attr.max}` 
                                          : t('form.enterValue', 'Wert eingeben')}
                                        value={currentValue as number || undefined}
                                        min={attr.min}
                                        max={attr.max}
                                        change={(e: any) => {
                                          setSetupAttributeValues(prev => ({ ...prev, [attrId]: e.value || 0 }));
                                        }}
                                      />
                                    ) : attr.valueType === 'BOOLEAN' ? (
                                      // Checkbox for boolean
                                      <div className="flex items-center">
                                        <CheckBoxComponent
                                          checked={currentValue === true || currentValue === 'true'}
                                          change={(e: any) => {
                                            setSetupAttributeValues(prev => ({ ...prev, [attrId]: Boolean(e.checked) }));
                                          }}
                                        />
                                        <label className="ml-2 text-sm text-gray-600">
                                          {t('form.yes', 'Ja')}
                                        </label>
                                      </div>
                                    ) : (
                                      // Text input for STRING or default
                                      <TextBoxComponent
                                        placeholder={t('form.enterValue', 'Wert eingeben')}
                                        value={currentValue as string || ''}
                                        change={(e: any) => {
                                          setSetupAttributeValues(prev => ({ ...prev, [attrId]: e.value || '' }));
                                        }}
                                      />
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Parallel Processing - only for non-singleResource types; disabled when long-term managed */}
                        {(() => {
                          return primaryResourceTypeId && currentSelectedResourceType && !currentSelectedResourceType.singleResource;
                        })() && (
                          <div className="mb-4">
                            <div className="flex items-center">
                              <CheckBoxComponent
                                checked={parallelProcessing}
                                disabled={outputMaterialLongRunning && longTermManaged}
                                change={(e: any) => {
                                  const checked = Boolean(e.checked);
                                  setParallelProcessing(checked);
                                }}
                              />
                              <label className="ml-2 text-sm font-medium text-gray-700">
                                {t('routing.form.parallelProcessing')}
                              </label>
                            </div>
                          </div>
                        )}

                        <div className="mb-4">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t('routing.form.timeType')} *
                          </label>
                          <DropDownListComponent
                            dataSource={timeTypeOptions}
                            fields={{ text: 'text', value: 'value' }}
                            placeholder={t('routing.form.timeType')}
                            value={timeType}
                            enabled={!(outputMaterialLongRunning && longTermManaged)}
                            change={(e: any) => {
                              setTimeType(e.value);
                              // Clear validation error when user makes selection
                              if (validationErrors.timeType) {
                                setValidationErrors(prev => ({ ...prev, timeType: false }));
                                setShowValidationErrors(false);
                              }
                            }}
                            cssClass={validationErrors.timeType ? 'e-error' : ''}
                          />
                          {showValidationErrors && validationErrors.timeType && (
                            <div className="text-red-500 text-sm mt-1">{t('routing.form.timeType')} {t('form.required')}</div>
                          )}
                        </div>

                        {/* Dynamic Duration Fields */}
                        {(timeType === 'FIXED_DURATION' || timeType === 'QUANTITY_DEPENDENT') && (
                          <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              {timeType === 'FIXED_DURATION' ? t('routing.form.totalDuration') : t('routing.form.durationPerPiece')} *
                              {outputMaterialLongRunning && longTermManaged && timeType === 'FIXED_DURATION' && (
                                <span
                                  className="inline-block ml-1 text-blue-600 cursor-help align-middle"
                                  title={t('routing.form.totalDurationMaxHint', 'Wenn Langzeit geführt aktiv ist, wird dieser Wert als maximale Dauer interpretiert.')}
                                  aria-label={t('routing.form.totalDurationMaxHint', 'Wenn Langzeit geführt aktiv ist, wird dieser Wert als maximale Dauer interpretiert.')}
                                >
                                  ⓘ
                                </span>
                              )}
                            </label>
                            <div className={validationErrors.duration ? 'border border-red-500 rounded' : ''}>
                              <DurationInput
                                value={timeType === 'FIXED_DURATION' ? (globalDuration ?? undefined) : (durationPerPiece ?? undefined)}
                                required={true}
                                allowFractionalSeconds={timeType === 'QUANTITY_DEPENDENT'}
                                onRef={(ref) => {
                                  if (timeType === 'FIXED_DURATION') {
                                    globalDurationRef.current = ref;
                                  } else {
                                    durationPerPieceRef.current = ref;
                                  }
                                }}
                                onChange={(value) => {
                                  setHasInteracted(prev => ({ ...prev, duration: true }));
                                  if (timeType === 'FIXED_DURATION') {
                                    setGlobalDuration(value);
                                  } else {
                                    setDurationPerPiece(value);
                                  }
                                }}
                              />
                            </div>
                            {showValidationErrors && validationErrors.duration && (
                              <div className="text-red-500 text-sm mt-1">
                                {timeType === 'FIXED_DURATION' ? t('routing.form.totalDuration') : t('routing.form.durationPerPiece')} {t('form.required')}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Resource Grid - Show if both primaryResourceTypeId and timeType are selected (filtering approach) */}
                        {/* All resources of the selected type are shown, filtering happens in the grid */}
                        {(() => {
                          const shouldShowGrid = primaryResourceTypeId && timeType && currentGridRows.length > 0;
                          return shouldShowGrid;
                        })() && (
                          <div className="mb-4">
                            <h6 className="text-sm font-semibold mb-2">
                              {timeType === 'RESOURCE_DEPENDENT' || timeType === 'QUANTITY_AND_RESOURCE_DEPENDENT' 
                                ? t('routing.form.durationPerResource')
                                : t('routing.form.allowedResources')
                              } *
                            </h6>
                            
                            <div className="h-60">
                              {(() => {
                                return null;
                              })()}
                              <GridComponent
                                ref={resourcesGridRef}
                                key={(() => {
                                  const gridKey = `resources-grid-${primaryResourceTypeId || ''}-${timeType || ''}-${parallelProcessing || false}-${columnVisibility?.showDuration ? 'duration' : 'no-duration'}-${columnVisibility?.showCapacityDemand ? 'cap' : 'no-cap'}-${columnVisibility?.showMinMaxCapacityDemand ? 'minmax' : 'no-minmax'}-${currentGridRows.length}`;
                                  return gridKey;
                                })()}
                                dataSource={currentGridRows}
                                enablePersistence={false}
                                height="100%"
                                allowSorting={true}
                                allowFiltering={false}
                                enableHeaderFocus={true}
                                locale="de-DE"
                                selectionSettings={{ type: 'Multiple' }}
                                dataBound={(_args: any) => {
                                  
                                  // Initialize selection after grid is bound
                                  // Use _initialSelected from gridRowsMemo (via currentGridRows which comes from gridRowsRef)
                                  // Use a small delay to ensure grid is fully rendered
                                  setTimeout(() => {
                                    if (resourcesGridRef.current && currentGridRows.length > 0) {
                                      const grid = resourcesGridRef.current;
                                      const selectedIndices: number[] = [];
                                      
                                      // Get initial selection from gridRowsMemo (stored in _initialSelected)
                                      // We need to match by resource UUID since currentGridRows might not have _initialSelected
                                      gridRowsMemo.forEach((memoRow, _index) => {
                                        if ((memoRow as any)._initialSelected) {
                                          // Find matching row in currentGridRows by UUID
                                          const matchingIndex = currentGridRows.findIndex(r => r.resource.uuid === memoRow.resource.uuid);
                                          if (matchingIndex >= 0) {
                                            selectedIndices.push(matchingIndex);
                                          }
                                        }
                                      });
                                      
                                      
                                      // Clear existing selection first
                                      try {
                                        grid.clearSelection();
                                      } catch (e) {
                                        console.error('🟢 dataBound - error clearing selection:', e);
                                      }
                                      
                                      // Select rows that should be selected
                                      if (selectedIndices.length > 0) {
                                        try {
                                          grid.selectRows(selectedIndices);

                                          // Verify selection was applied
                                          setTimeout(() => {
                                            grid.getSelectedRowIndexes();
                                          }, 50);
                                        } catch (e) {
                                          console.error('🟢 dataBound - error selecting rows:', e);
                                        }
                                      }
                                    }
                                  }, 0);
                                }}
                                rowSelected={(_args: any) => {
                                  // Selection is managed by Grid - no need to update state
                                  // This prevents re-renders when selection changes
                                }}
                                rowDeselected={(_args: any) => {
                                  // Selection is managed by Grid - no need to update state
                                  // This prevents re-renders when selection changes
                                }}
                                dataStateChange={(_args: any) => {
                                }}
                                dataSourceChanged={(_args: any) => {
                                }}
                              >
                                <ColumnsDirective>
                                  {(() => {
                                    return null;
                                  })()}
                                  {/* Checkbox column - Always visible */}
                                  <ColumnDirective type="checkbox" width="50" />
                                  {/* Resource Name - Always visible */}
                                  <ColumnDirective
                                    field="resource.name"
                                    headerText={t('form.resource')}
                                    width="auto"
                                    template={(props: any) => {
                                      const item = props.data || props.rowData || props;
                                      return <span>{item.resource.name}</span>;
                                    }}
                                  />
                                  {/* Duration - Only visible for resource-dependent time types */}
                                  <ColumnDirective
                                    field="duration"
                                    headerText={(() => {
                                      const headerTextValue = timeType === 'RESOURCE_DEPENDENT' 
                                        ? t('routing.form.totalDuration')
                                        : timeType === 'QUANTITY_AND_RESOURCE_DEPENDENT'
                                        ? t('routing.form.durationPerPiece')
                                        : t('routing.form.totalDuration'); // Fallback (should not happen if visible is correct)
                                      return headerTextValue;
                                    })()}
                                    width={200}
                                    visible={(() => {
                                      const visibleValue = columnVisibility?.showDuration === true;
                                      return visibleValue;
                                    })()}
                                    template={(props: any) => {
                                      // Hidden columns render no template content. Stale template portals of hidden cells
                                      // made React throw "removeChild" when the dialog unmounted after OK.
                                      if (!(columnVisibility?.showDuration === true)) return null;
                                      const item = props.data || props.rowData || props;
                                      const durationValue = timeType === 'RESOURCE_DEPENDENT' 
                                        ? (item.duration ?? undefined) 
                                        : timeType === 'QUANTITY_AND_RESOURCE_DEPENDENT'
                                        ? (item.durationPerPiece ?? undefined)
                                        : undefined;
                                      
                                      // Check if row is selected by reading from Grid
                                      const isSelected = resourcesGridRef.current 
                                        ? resourcesGridRef.current.getSelectedRowIndexes().includes(props.rowIndex)
                                        : false;
                                      
                                      return (
                                        <DurationInput
                                          value={durationValue}
                                          required={true}
                                          allowFractionalSeconds={timeType === 'QUANTITY_AND_RESOURCE_DEPENDENT'}
                                          disabled={!isSelected}
                                          onChange={(value: number | null) => {
                                            setHasInteracted(prev => ({ ...prev, duration: true }));
                                            if (timeType === 'RESOURCE_DEPENDENT') {
                                              handleResourceDurationChangeRef.current(item.resource.uuid!, value || 0);
                                            } else if (timeType === 'QUANTITY_AND_RESOURCE_DEPENDENT') {
                                              handleResourceDurationPerPieceChangeRef.current(item.resource.uuid!, value || 0);
                                            }
                                          }}
                                        />
                                      );
                                    }}
                                  />
                                  {/* Capacity Demand - Visible based on columnVisibility */}
                                  <ColumnDirective
                                    field="capacityDemand"
                                    headerText={t('routing.form.capacityDemand')}
                                    width={180}
                                    visible={columnVisibility?.showCapacityDemand === true}
                                    template={(props: any) => {
                                      // Hidden columns render no template content. Stale template portals of hidden cells
                                      // made React throw "removeChild" when the dialog unmounted after OK.
                                      if (!(columnVisibility?.showCapacityDemand === true)) return null;
                                      const item = props.data || props.rowData || props;
                                      const capacityDemand = item.capacityDemand !== undefined && item.capacityDemand !== null 
                                        ? item.capacityDemand 
                                        : undefined;
                                      
                                      // Check if row is selected by reading from Grid
                                      const isSelected = resourcesGridRef.current 
                                        ? resourcesGridRef.current.getSelectedRowIndexes().includes(props.rowIndex)
                                        : false;
                                      
                                      const maxValue = 1.0;
                                      const minValue = 0;
                                      const displayValue = capacityDemand !== undefined 
                                        ? capacityDemand
                                        : undefined;
                                      
                                      // Validation: For both: 0 < value <= 1.0 (internal)
                                      const hasError = isSelected && capacityDemand !== undefined && 
                                        (capacityDemand <= minValue || capacityDemand > maxValue);
                                      
                                      return (
                                        <NumericTextBoxComponent
                                          placeholder="0.0"
                                          value={displayValue !== undefined ? displayValue : undefined}
                                          change={(e: any) => {
                                            const inputValue = e.value !== null && e.value !== undefined ? e.value : null;
                                            if (inputValue !== null) {
                                              // Clamp value to valid range (0 < value <= 1.0)
                                              let clampedValue: number | undefined = undefined;
                                              if (inputValue > 0 && inputValue <= 1.0) {
                                                clampedValue = inputValue;
                                              } else if (inputValue > 1.0) {
                                                clampedValue = 1.0;
                                              }
                                              handleResourceCapacityDemandChangeRef.current(item.resource.uuid!, clampedValue);
                                            } else {
                                              handleResourceCapacityDemandChangeRef.current(item.resource.uuid!, undefined);
                                            }
                                          }}
                                          disabled={!isSelected}
                                          cssClass={`text-center ${hasError ? 'e-error' : ''}`}
                                          min={0}
                                          max={1.0}
                                          step={0.1}
                                          format="N2"
                                          title="Wert muss zwischen 0.0 (exklusiv) und 1.0 (inklusiv) sein"
                                        />
                                      );
                                    }}
                                  />
                                  {/* Min Capacity Demand - Visible when parallelProcessing is active */}
                                  <ColumnDirective
                                    field="minCapacityDemand"
                                    headerText={t('routing.form.minCapacityDemand')}
                                    width={120}
                                    visible={columnVisibility?.showMinMaxCapacityDemand === true}
                                    template={(props: any) => {
                                      // Hidden columns render no template content. Stale template portals of hidden cells
                                      // made React throw "removeChild" when the dialog unmounted after OK.
                                      if (!(columnVisibility?.showMinMaxCapacityDemand === true)) return null;
                                      const item = props.data || props.rowData || props;
                                      // Check if row is selected by reading from Grid
                                      const isSelected = resourcesGridRef.current 
                                        ? resourcesGridRef.current.getSelectedRowIndexes().includes(props.rowIndex)
                                        : false;
                                      
                                      return (
                                        <NumericTextBoxComponent
                                          placeholder="0"
                                          value={item.minCapacityDemand ?? undefined}
                                          change={(e: any) => {
                                            const inputValue = e.value !== null && e.value !== undefined ? e.value : null;
                                            if (inputValue !== null) {
                                              // Ensure integer value
                                              const intValue = Math.round(inputValue);
                                              if (intValue >= 0) {
                                                handleResourceMinCapacityDemandChangeRef.current(item.resource.uuid!, intValue);
                                              }
                                            } else {
                                              handleResourceMinCapacityDemandChangeRef.current(item.resource.uuid!, 0);
                                            }
                                          }}
                                          disabled={!isSelected}
                                          cssClass="text-center"
                                          min={0}
                                          step={1}
                                          format="n0"
                                          title="Wert muss eine ganze Zahl ≥ 0 sein"
                                        />
                                      );
                                    }}
                                  />
                                  {/* Max Capacity Demand - Visible when parallelProcessing is active */}
                                  <ColumnDirective
                                    field="maxCapacityDemand"
                                    headerText={t('routing.form.maxCapacityDemand')}
                                    width={120}
                                    visible={columnVisibility?.showMinMaxCapacityDemand === true}
                                    template={(props: any) => {
                                      // Hidden columns render no template content. Stale template portals of hidden cells
                                      // made React throw "removeChild" when the dialog unmounted after OK.
                                      if (!(columnVisibility?.showMinMaxCapacityDemand === true)) return null;
                                      const item = props.data || props.rowData || props;
                                      // Check if row is selected by reading from Grid
                                      const isSelected = resourcesGridRef.current 
                                        ? resourcesGridRef.current.getSelectedRowIndexes().includes(props.rowIndex)
                                        : false;
                                      
                                      return (
                                        <NumericTextBoxComponent
                                          placeholder="0"
                                          value={item.maxCapacityDemand ?? undefined}
                                          change={(e: any) => {
                                            const inputValue = e.value !== null && e.value !== undefined ? e.value : null;
                                            if (inputValue !== null) {
                                              // Ensure integer value
                                              const intValue = Math.round(inputValue);
                                              if (intValue >= 0) {
                                                handleResourceMaxCapacityDemandChangeRef.current(item.resource.uuid!, intValue);
                                              }
                                            } else {
                                              handleResourceMaxCapacityDemandChangeRef.current(item.resource.uuid!, 0);
                                            }
                                          }}
                                          disabled={!isSelected}
                                          cssClass="text-center"
                                          min={0}
                                          step={1}
                                          format="n0"
                                          title="Wert muss eine ganze Zahl ≥ 0 sein"
                                        />
                                      );
                                    }}
                                  />
                                </ColumnsDirective>
                                <Inject services={[Sort]} />
                              </GridComponent>
                            </div>
                          </div>
                        )}
              
                      {/* Resources validation message */}
                      {showValidationErrors && validationErrors.resources && (
                        <div className="text-red-500 text-sm mt-2">
                          {t('routing.form.resources')} {t('form.required')}
                        </div>
                      )}
                      </div>
                    </div>
                  ),
                },

                // Input Quantities Tab
                {
                  header: t('routing.form.inputQuantitiesTab'),
                  content: () => {
                    // Use materials and unitTypes directly from state
                    // CRITICAL: Use materials directly, not a new array, to maintain reference
                    const currentMaterials = materials || [];
                    const currentUnitTypes = unitTypes || [];

                    // Debug logging
                    
                    return (
                      <div className="p-4" style={{ height: '100%', overflowY: 'auto' }}>
                        {/* Input Quantities Section - Inline Editing Grid */}
                        <div>
                          <div className="border rounded-lg overflow-hidden">
                            {/* CrudPage in localMode with InputQuantitySubDialog as the FormComponent -
                             * the same pattern as the operations grid in RoutingFormDialog.
                             * A sub-dialog instead of inline editing avoids React 19 + Syncfusion race conditions. */}
                            <CrudPage<InputQuantityRow>
                              refreshKey={inputQuantitiesRefreshKey}
                              titleKey="routing.form.inputQuantities"
                              showTitle={false}
                              noPadding={true}
                              localMode={true}
                              gridMeta={[
                                {
                                  field: 'materialName',
                                  headerKey: 'routing.form.material',
                                  width: 240,
                                },
                                {
                                  field: 'quantity',
                                  headerKey: 'routing.form.quantity',
                                  width: 120,
                                  align: 'Right',
                                },
                                {
                                  field: 'unitText',
                                  headerKey: 'routing.form.unit',
                                  width: 120,
                                },
                                {
                                  field: 'scopeText',
                                  headerKey: 'routing.form.inputQuantityForm.scope',
                                  width: 160,
                                },
                              ]}
                              fetchData={async () => {
                                const scopeLabel = (s: string | undefined) => {
                                  switch (s) {
                                    case 'PER_BATCH':
                                      return t('routing.form.inputQuantityForm.perBatch', { defaultValue: 'Pro Charge' }) as string;
                                    case 'PER_CAMPAIGN':
                                      return t('routing.form.inputQuantityForm.perCampaign', { defaultValue: 'Pro Kampagne' }) as string;
                                    case 'PER_PIECE':
                                    default:
                                      return t('routing.form.inputQuantityForm.perPiece', { defaultValue: 'Pro Stück' }) as string;
                                  }
                                };
                                return inputQuantities.map((q, idx) => ({
                                  id: String(idx),
                                  materialId: q.materialId || '',
                                  quantity: q.quantity ?? 0,
                                  unit: q.unit || 'PIECE',
                                  quantityScope: (q.quantityScope ?? 'PER_PIECE') as QuantityScope,
                                  materialName: currentMaterials.find((m: MaterialDto) => m.uuid === q.materialId)?.name || '',
                                  unitText: currentUnitTypes.find((u: { text: string; value: string }) => u.value === q.unit)?.text || q.unit || '',
                                  scopeText: scopeLabel(q.quantityScope),
                                }));
                              }}
                              saveData={async (item: InputQuantityRow) => {
                                const selectedMaterial = materials.find((m) => m.uuid === item.materialId);
                                const resolvedUnit = selectedMaterial?.baseUnitType || item.unit || 'PIECE';
                                const resolvedScope = item.quantityScope ?? 'PER_PIECE';
                                if (item.id !== undefined && item.id !== null && item.id !== '' && /^\d+$/.test(String(item.id))) {
                                  // Edit existing row
                                  const idx = parseInt(String(item.id), 10);
                                  setInputQuantities(prev =>
                                    prev.map((q, i) => (i === idx
                                      ? { ...q, materialId: item.materialId, quantity: item.quantity, unit: resolvedUnit, quantityScope: resolvedScope }
                                      : q)),
                                  );
                                } else {
                                  // Add new row
                                  setInputQuantities(prev => [
                                    ...prev,
                                    { materialId: item.materialId, quantity: item.quantity, unit: resolvedUnit, quantityScope: resolvedScope, index: prev.length },
                                  ]);
                                }
                                setInputQuantitiesRefreshKey(k => k + 1);
                              }}
                              deleteData={async (id: string) => {
                                const idx = parseInt(String(id), 10);
                                if (Number.isNaN(idx)) return;
                                setInputQuantities(prev =>
                                  prev.filter((_, i) => i !== idx).map((item, i) => ({ ...item, index: i })),
                                );
                                setInputQuantitiesRefreshKey(k => k + 1);
                              }}
                              FormComponent={InputQuantitySubDialog}
                              formProps={{ materials }}
                              showAddButton={true}
                              showEditButton={true}
                              showDeleteButton={true}
                              showSearchButton={false}
                              allowFiltering={false}
                              allowSorting={false}
                              showSummary={false}
                              hasPriority={false}
                              deletionMode="Single"
                            />
                          </div>
                        </div>
                      </div>
                    );
                  },
                },
              ]}
            />
            </div>
            </div>
          </div>
    </form>
    );
  };


  // Handle time type changes when parallel processing or resource type changes
  // When primaryResourceTypeId changes, clear timeType (user must select again)
  // When timeType is set but not in allowed types, clear it
  useEffect(() => {
    const allowedTimeTypes = getTimeTypeOptions();
    // If timeType is set but not in allowed types, clear it
    if (timeType && !allowedTimeTypes.some(option => option.value === timeType)) {
      setTimeType('');
    }
    // Note: We don't auto-select timeType when primaryResourceTypeId changes
    // User must explicitly select timeType
  }, [parallelProcessing, primaryResourceTypeId, getTimeTypeOptions, timeType]);

  // Update gridRows when globalDuration changes (for FIXED_DURATION)
  // 0 is a valid value, so we check for >= 0 instead of > 0
  useEffect(() => {
    if (timeType === 'FIXED_DURATION' && globalDuration !== null && globalDuration !== undefined && globalDuration >= 0) {
      setGridRows(prev => prev.map(item => ({
        ...item,
        // Update duration only when it has not been set individually yet (undefined or null);
        // 0 is a valid value and must be kept
        duration: (item.duration === undefined || item.duration === null || item.duration === 0) 
          ? globalDuration 
          : item.duration
      })));
    }
  }, [globalDuration, timeType]);

  // Update gridRows when durationPerPiece changes (for QUANTITY_DEPENDENT)
  // 0 is a valid value, so we check for >= 0 instead of > 0
  useEffect(() => {
    if (timeType === 'QUANTITY_DEPENDENT' && durationPerPiece !== null && durationPerPiece !== undefined && durationPerPiece >= 0) {
      setGridRows(prev => prev.map(item => ({
        ...item,
        // Update durationPerPiece only when it has not been set individually yet (undefined or null);
        // 0 is a valid value and must be kept
        durationPerPiece: (item.durationPerPiece === undefined || item.durationPerPiece === null || item.durationPerPiece === 0) 
          ? durationPerPiece 
          : item.durationPerPiece
      })));
    }
  }, [durationPerPiece, timeType]);

  // Input quantities management functions
  // Resource value change handlers - these update gridRows state for duration, capacity, etc.
  // NOTE: Selection changes are NOT handled here - they're managed by the Grid component itself
  // This prevents re-renders when selection changes

  const handleResourceDurationChange = React.useCallback((resourceUuid: string, duration: number) => {
    setGridRows(prev => prev.map(item => 
      item.resource.uuid === resourceUuid 
        ? { ...item, duration }
        : item
    ));
  }, []);

  const handleResourceDurationPerPieceChange = React.useCallback((resourceUuid: string, durationPerPiece: number) => {
    setGridRows(prev => prev.map(item => 
      item.resource.uuid === resourceUuid 
        ? { ...item, durationPerPiece }
        : item
    ));
  }, []);

  const handleResourceCapacityDemandChange = React.useCallback((resourceUuid: string, capacityDemand: number | undefined) => {
    setGridRows(prev => prev.map(item => 
      item.resource.uuid === resourceUuid 
        ? { ...item, capacityDemand }
        : item
    ));
  }, []);

  // Integer handling for min/max capacity demand
  const handleResourceMinCapacityDemandChange = React.useCallback((resourceUuid: string, minCapacityDemand: number) => {
    // Ensure integer value
    const intValue = Math.round(minCapacityDemand);
    if (intValue >= 0) {
      setGridRows(prev => prev.map(item => 
        item.resource.uuid === resourceUuid 
          ? { ...item, minCapacityDemand: intValue }
          : item
      ));
    }
  }, []);

  const handleResourceMaxCapacityDemandChange = React.useCallback((resourceUuid: string, maxCapacityDemand: number) => {
    // Ensure integer value
    const intValue = Math.round(maxCapacityDemand);
    if (intValue >= 0) {
      setGridRows(prev => prev.map(item => 
        item.resource.uuid === resourceUuid 
          ? { ...item, maxCapacityDemand: intValue }
          : item
      ));
    }
  }, []);

  // Refs for handlers to prevent re-renders in Grid templates
  // NOTE: handleResourceSelectionChange removed - selection is managed by Grid
  const handleResourceDurationChangeRef = React.useRef(handleResourceDurationChange);
  const handleResourceDurationPerPieceChangeRef = React.useRef(handleResourceDurationPerPieceChange);
  const handleResourceCapacityDemandChangeRef = React.useRef(handleResourceCapacityDemandChange);
  const handleResourceMinCapacityDemandChangeRef = React.useRef(handleResourceMinCapacityDemandChange);
  const handleResourceMaxCapacityDemandChangeRef = React.useRef(handleResourceMaxCapacityDemandChange);

  React.useEffect(() => {
    handleResourceDurationChangeRef.current = handleResourceDurationChange;
  }, [handleResourceDurationChange]);

  React.useEffect(() => {
    handleResourceDurationPerPieceChangeRef.current = handleResourceDurationPerPieceChange;
  }, [handleResourceDurationPerPieceChange]);

  React.useEffect(() => {
    handleResourceCapacityDemandChangeRef.current = handleResourceCapacityDemandChange;
  }, [handleResourceCapacityDemandChange]);

  React.useEffect(() => {
    handleResourceMinCapacityDemandChangeRef.current = handleResourceMinCapacityDemandChange;
  }, [handleResourceMinCapacityDemandChange]);

  React.useEffect(() => {
    handleResourceMaxCapacityDemandChangeRef.current = handleResourceMaxCapacityDemandChange;
  }, [handleResourceMaxCapacityDemandChange]);

  const handleSave = async () => {
    if (isSaving) return;
    setIsSaving(true);

    // Commit duration values before validation
    if (globalDurationRef.current) {
      globalDurationRef.current.commit();
    }
    if (durationPerPieceRef.current) {
      durationPerPieceRef.current.commit();
    }
    if (minDistancePredecessorRef.current) {
      minDistancePredecessorRef.current.commit();
    }
    if (maxDistancePredecessorRef.current) {
      maxDistancePredecessorRef.current.commit();
    }
    
    // CRITICAL: Read actual grid selection directly from the grid
    // This ensures we use what the user actually sees, not stale state
    // IMPORTANT: Use gridRowsRefForContent.current to get the latest gridRows value
    // (avoid stale closure issue - handleSave might have been created with old gridRows)
    const currentGridRowsFromRef = gridRowsRefForContent.current;
    let currentGridRowsForValidation = currentGridRowsFromRef;
    let selectedResourcesListForValidation: typeof currentGridRowsFromRef = [];
    
    // Declare selectedResourceUuids outside the if block so it's always available
    const selectedResourceUuids = new Set<string>();
    
    
    if (resourcesGridRef.current && currentGridRowsFromRef.length > 0) {
      const grid = resourcesGridRef.current;
      const selectedRowIndexes = grid.getSelectedRowIndexes();

      if (selectedRowIndexes.length > 0) {
        selectedRowIndexes.forEach((index: number) => {
          if (index >= 0 && index < currentGridRowsFromRef.length) {
            const row = currentGridRowsFromRef[index];
            if (row && row.resource && row.resource.uuid) {
              selectedResourceUuids.add(row.resource.uuid);
            }
          }
        });
      }

      
      // Use currentGridRowsFromRef for validation (selection is read from Grid, not state)
      // No need to update state - selection is managed by Grid
      currentGridRowsForValidation = currentGridRowsFromRef;
    } else {
      // Fallback: use currentGridRowsFromRef directly
      currentGridRowsForValidation = currentGridRowsFromRef;
    }

    // Fallback: when the grid returns no selection (for example because the tab component
    // unmounts the inactive resources tab), read the resources from value.operationModes -
    // that holds the already saved selection. This way the user does not have to click the
    // resource again after editing only another tab.
    if (selectedResourceUuids.size === 0 && Array.isArray(value?.operationModes)) {
      value!.operationModes!.forEach((om: any) => {
        if (om?.resourceId) {
          selectedResourceUuids.add(om.resourceId);
        }
      });
      // currentGridRowsForValidation must contain all required data - when the grid is
      // unmounted we still have gridRowsRefForContent, which holds the pre-mount data.
      if (currentGridRowsForValidation.length === 0 && gridRowsRefForContent.current.length > 0) {
        currentGridRowsForValidation = gridRowsRefForContent.current;
      }
    }
    
    // Clear previous validation errors
    setValidationErrors({});
    setShowValidationErrors(false);
    
    // Validate required fields
    const errors: string[] = [];
    const newValidationErrors: {[key: string]: boolean} = {};
    
    if (!name.trim()) {
      errors.push(t('routing.form.operationName') + ' ' + t('form.required'));
      newValidationErrors.name = true;
    }
    
    if (!primaryResourceTypeId) {
      errors.push(t('routing.form.primaryResourceType') + ' ' + t('form.required'));
      newValidationErrors.primaryResourceType = true;
    }
    
    if (!timeType) {
      errors.push(t('routing.form.timeType') + ' ' + t('form.required'));
      newValidationErrors.timeType = true;
    }
    
    // Validate duration based on time type - only validate if the field is visible and user has interacted
    if (timeType === 'FIXED_DURATION' || timeType === 'QUANTITY_DEPENDENT') {
      
      if (timeType === 'FIXED_DURATION' && (globalDuration === null || globalDuration < 0)) {
        errors.push(t('routing.form.totalDuration') + ' ' + t('form.required'));
        newValidationErrors.duration = true;
      }
      
      if (timeType === 'QUANTITY_DEPENDENT' && (durationPerPiece === null || durationPerPiece < 0)) {
        errors.push(t('routing.form.durationPerPiece') + ' ' + t('form.required'));
        newValidationErrors.duration = true;
      }
    }
    
    // Validate resources - at least one resource must be selected for all time types
    // Selection is read directly from Grid (selectedResourceUuids), not from state
    selectedResourcesListForValidation = currentGridRowsForValidation.filter((row: any) => 
      selectedResourceUuids.has(row.resource?.uuid)
    );
    
    if (selectedResourcesListForValidation.length === 0) {
      errors.push(t('routing.form.resources') + ' ' + t('form.required'));
      newValidationErrors.resources = true;
    }
    
    // Validate resource durations when required (only for resource-dependent time types)
    // Duration validation: only validate if TimeType is resource-dependent AND resource is selected
    if (timeType === 'RESOURCE_DEPENDENT' || timeType === 'QUANTITY_AND_RESOURCE_DEPENDENT') {
      const hasInvalidDuration = selectedResourcesListForValidation.some(item => {
        if (timeType === 'RESOURCE_DEPENDENT') {
          // For RESOURCE_DEPENDENT: validate duration field (>= 0 is valid)
          return item.duration === undefined || item.duration === null || item.duration < 0;
        } else {
          // For QUANTITY_AND_RESOURCE_DEPENDENT: validate durationPerPiece field (>= 0 is valid)
          return item.durationPerPiece === undefined || item.durationPerPiece === null || item.durationPerPiece < 0;
        }
      });
      
      if (hasInvalidDuration) {
        errors.push(t('routing.form.resourceDuration') + ' ' + t('form.required'));
        newValidationErrors.resources = true;
      }
    }
    
    // Set validation errors
    
    // Force update validation errors state
    setValidationErrors(newValidationErrors);
    setShowValidationErrors(true);
    
    // If there are validation errors, don't save
    if (errors.length > 0) {
      setIsSaving(false);
      showToast(
        'warning',
        t('common.validation', { defaultValue: 'Validierung' }),
        errors.join('\n'),
      );
      return;
    }
    
    
    // Use selectedResourcesListForValidation which was synced with grid selection
    const finalSelectedResourcesList = selectedResourcesListForValidation;

    // Create execution mode from form fields
    const selectedResourceType = resourceTypes.find(rt => rt.uuid === primaryResourceTypeId);
    
    // Note: MATERIAL attribute values are automatically set in the backend
    // (see RoutingService.ensureAutomaticSetupAttributeValue)
    // We only send manually set attribute values from the frontend (excluding MATERIAL type)
    const currentSelectedResourceType = resourceTypes.find(rt => rt.uuid === primaryResourceTypeId);
    
    // Filter out MATERIAL attribute values - these are set automatically by the backend
    const materialAttributeIds = new Set<string>();
    if (currentSelectedResourceType) {
      // Collect all MATERIAL attribute IDs
      currentSelectedResourceType.setupAttributes
        ?.filter(attr => attr.valueType === 'MATERIAL')
        .forEach(attr => {
          if (attr.uuid) materialAttributeIds.add(attr.uuid);
        });
    }
    
    // Remove MATERIAL attribute values before sending
    const finalSetupAttributeValues: {[attributeId: string]: string | number | boolean} = {};
    Object.entries(setupAttributeValues).forEach(([attrId, value]) => {
      if (!materialAttributeIds.has(attrId)) {
        finalSetupAttributeValues[attrId] = value;
      }
    });
    
    
    // Create an array of OperationModeDto (one per resource)
    // Use finalSelectedResourcesList which is synced with grid selection
    const operationModes: OperationModeDto[] = finalSelectedResourcesList.map((item, _index) => {
      // For FIXED_DURATION: use globalDuration for all resources when item.duration is not set;
      // 0 is a valid value and must be kept
      const durationSeconds = timeType === 'FIXED_DURATION' 
        ? (item.duration !== undefined && item.duration !== null
            ? item.duration 
            : (globalDuration !== null && globalDuration !== undefined
                ? globalDuration 
                : undefined))
        : timeType === 'RESOURCE_DEPENDENT' 
          ? (item.duration !== undefined && item.duration !== null ? item.duration : undefined)
          : undefined;
          
      // For QUANTITY_DEPENDENT: use durationPerPiece for all resources when item.durationPerPiece is not set;
      // 0 is a valid value and must be kept
      const durationPerPieceSeconds = timeType === 'QUANTITY_DEPENDENT' 
        ? (item.durationPerPiece !== undefined && item.durationPerPiece !== null
            ? item.durationPerPiece 
            : (durationPerPiece !== null && durationPerPiece !== undefined
                ? durationPerPiece 
                : undefined))
        : timeType === 'QUANTITY_AND_RESOURCE_DEPENDENT'
          ? (item.durationPerPiece !== undefined && item.durationPerPiece !== null ? item.durationPerPiece : undefined)
          : undefined;
          
      
      // Use resource.uuid (id field was removed from ResourceDto)
      const resourceIdentifier = item.resource.uuid;
      if (!resourceIdentifier) {
        console.error('Resource has no uuid:', item.resource);
      }
      
      // Determine capacityDemand based on resource type and parallelProcessing
      const isSingleResource = currentSelectedResourceType?.singleResource === true;
      
      let capacityDemandValue: number | undefined = undefined;
      let minCapacityDemandValue: number | undefined = undefined;
      let maxCapacityDemandValue: number | undefined = undefined;
      
      if (isSingleResource) {
        // SingleResource: always 1.0
        capacityDemandValue = 1.0;
      } else if (parallelProcessing && !isSingleResource) {
        // No SingleResource with parallelProcessing: use min/max
        minCapacityDemandValue = item.minCapacityDemand || undefined;
        maxCapacityDemandValue = item.maxCapacityDemand || undefined;
      } else {
        // Use capacityDemand
        capacityDemandValue = item.capacityDemand !== undefined && item.capacityDemand !== null ? item.capacityDemand : undefined;
      }
      
      return {
        uuid: item.operationModeUuid, // Include UUID if this is an existing operation mode
        resourceId: resourceIdentifier!,
        resourceName: item.resource.name!,
        durationSeconds,
        durationPerPieceSeconds,
        capacityDemand: capacityDemandValue,
        minCapacityDemand: minCapacityDemandValue,
        maxCapacityDemand: maxCapacityDemandValue
      };
    });
    

    // Convert attribute values to arrays of OperationAttributeValueDto format
    const setupAttributeValuesArray = Object.keys(finalSetupAttributeValues).length > 0
      ? Object.entries(finalSetupAttributeValues).map(([attributeDefinitionId, value]) => ({
          attributeDefinitionId,
          value: String(value),
          attributeType: 'SETUP'
        }))
      : undefined;
    
    const operationData: OperationWithId = {
      ...value,
      id: value?.id || generateTempUUID(),
      uuid: value?.uuid || generateTempUUID(),
      name,
      description,
      // Read through the ref in case handleSave is called from a stale closure (Syncfusion
      // footerTemplate). That guarantees entries added via the sub-dialog are included.
      inputQuantities: inputQuantitiesRef.current,
      parallelProcessing,
      longTermManaged: outputMaterialLongRunning ? longTermManaged : false,
      timeType,
      primaryResourceTypeId: primaryResourceTypeId || undefined, // Convert empty string to undefined
      primaryResourceTypeName: selectedResourceType?.name || '',
      // For FIXED_DURATION and QUANTITY_DEPENDENT, use direct fields
      durationSeconds: timeType === 'FIXED_DURATION' ? (globalDuration ?? undefined) : undefined,
      durationPerPieceSeconds: timeType === 'QUANTITY_DEPENDENT' ? (durationPerPiece ?? undefined) : undefined,
      operationModes, // Array of operation modes (one per resource)
      setupAttributeValues: setupAttributeValuesArray,
      // Capacity demand type
      capacityDemandType: capacityDemandTypeRef.current,
      // Distance to predecessor (only for operations with operationNumber > 1)
      // Important: 0 is a valid value, so we must check explicitly for null/undefined
      // minDistancePredecessor must always be set (default 0) for operations with operationNumber > 1
      minDistancePredecessor: value?.operationNumber && value.operationNumber > 1 
        ? (minDistancePredecessor !== null && minDistancePredecessor !== undefined ? minDistancePredecessor : 0)
        : undefined,
      maxDistancePredecessor: value?.operationNumber && value.operationNumber > 1 
        ? (maxDistancePredecessor !== null && maxDistancePredecessor !== undefined ? maxDistancePredecessor : undefined)
        : undefined
    };
    
    
    // Ensure primaryResourceTypeId is not empty string
    if (!operationData.primaryResourceTypeId || operationData.primaryResourceTypeId.trim() === '') {
      console.error('❌ OperationDetailDialog - primaryResourceTypeId is missing or empty!', {
        primaryResourceTypeId: operationData.primaryResourceTypeId,
        primaryResourceTypeIdState: primaryResourceTypeId,
        selectedResourceType: selectedResourceType
      });
    }
    
    try {
      await Promise.resolve(onSave(operationData));
    } finally {
      setIsSaving(false);
    }
  };

  // Determine if this is a new operation (temp UUID) or existing (real UUID)
  const isNewOperation = !value?.uuid || isTempUUID(value.uuid);
  
  // Footer template - use regular function (not useCallback) as Syncfusion expects a function reference
  // IMPORTANT: Must be defined before any conditional returns to follow Rules of Hooks
  const footerTemplate = () => (
    <div className="flex justify-end gap-2 p-2">
      <ButtonComponent
        onClick={onClose}
        cssClass="e-outline e-btn e-outline"
        type="button"
      >
        {t('actions.cancel')}
      </ButtonComponent>
      <ButtonComponent
        onClick={handleSave}
        cssClass="e-primary e-btn e-primary"
        type="button"
        disabled={isSaving}
      >
        {isSaving ? t('actions.saving') : t('actions.ok')}
      </ButtonComponent>
    </div>
  );

  // Use dialogContent directly - it's now a regular function that always uses latest values
  // No need to memoize since it's recreated on every render with latest gridRows
  
  // Log current state before rendering DialogComponent

  if (loading) {
    return (
      <div className="h-64 flex items-center justify-center">
        <div className="text-center text-gray-500">
          <div className="text-md font-medium">{t('common.loading', 'Loading...')}</div>
        </div>
      </div>
    );
  }
  
  return (
    <>
      <DialogComponent
        visible={true}
        isModal={true}
        header={isNewOperation ? t('actions.add') : t('actions.edit')}
        showCloseIcon={true}
        closeOnEscape={true}
        width="1000px"
        height="900px"
        close={onClose}
        target="body"
        animationSettings={{ effect: 'None' }}
        cssClass="operation-detail-dialog"
        zIndex={10000}
        statelessTemplates={[]}
        enableRtl={false}
        footerTemplate={footerTemplate}
      >
        {/* Render as children, not as the `content` template: a new template function after
            each state change makes Syncfusion rebuild the body, which resets every scroll position. */}
        {dialogContent()}
      </DialogComponent>
    </>
  );
};

type QuantityScope = 'PER_PIECE' | 'PER_BATCH' | 'PER_CAMPAIGN';

/** Row type for the CrudPage embedded in OperationDetailDialog. */
type InputQuantityRow = {
  id: string;
  materialId: string;
  quantity: number;
  unit: string;
  quantityScope: QuantityScope;
  materialName: string;
  unitText: string;
  scopeText: string;
};

/**
 * Sub-dialog for creating/editing a single OperationInputQuantity.
 * Same pattern as OperationDetailDialog: DialogComponent + footerTemplate with an OK button.
 * 'OK' (not 'Save'), because the actual persistence happens only on the OK of the
 * enclosing OperationDetailDialog (and then on the routing form).
 */
// `materials` is optional only because CrudPage types FormComponent as ComponentType<CrudFormProps<T>>;
// the caller must pass it through formProps. The empty default is a type-system workaround, not a
// valid state: without materials the dropdown is empty.
const InputQuantitySubDialog: React.FC<CrudFormProps<InputQuantityRow> & { materials?: MaterialDto[] }> = ({
  value,
  onClose,
  onSave,
  materials = [],
}) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const isNew = !value || !value.id;
  const [materialId, setMaterialId] = useState<string>(value?.materialId ?? '');
  const [quantity, setQuantity] = useState<number>(value?.quantity ?? 0);
  const [quantityScope, setQuantityScope] = useState<QuantityScope>(value?.quantityScope ?? 'PER_PIECE');
  const [errors, setErrors] = useState<{ material?: string; quantity?: string }>({});

  const scopeOptions = React.useMemo(() => [
    { value: 'PER_PIECE', text: t('routing.form.inputQuantityForm.perPiece', { defaultValue: 'Pro Stück' }) as string },
    { value: 'PER_BATCH', text: t('routing.form.inputQuantityForm.perBatch', { defaultValue: 'Pro Charge' }) as string },
    { value: 'PER_CAMPAIGN', text: t('routing.form.inputQuantityForm.perCampaign', { defaultValue: 'Pro Kampagne' }) as string },
  ], [t]);

  const handleOk = async () => {
    const validationErrors: typeof errors = {};
    const missing: string[] = [];
    if (!materialId) {
      validationErrors.material = t('validation.required', { defaultValue: 'Pflichtfeld' }) as string;
      missing.push(t('routing.form.material', { defaultValue: 'Material' }) as string);
    }
    if (!(quantity >= 0)) {
      validationErrors.quantity = t('validation.required', { defaultValue: 'Pflichtfeld' }) as string;
      missing.push(t('routing.form.quantity', { defaultValue: 'Menge' }) as string);
    }
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      showToast(
        'warning',
        t('common.validation', { defaultValue: 'Validierung' }) as string,
        missing.map(m => `${m}: ${t('form.required', { defaultValue: 'erforderlich' })}`).join('\n'),
      );
      return;
    }
    const selectedMaterial = materials.find((m) => m.uuid === materialId);
    await onSave({
      id: value?.id ?? '',
      materialId,
      quantity,
      quantityScope,
      unit: selectedMaterial?.baseUnitType || value?.unit || 'PIECE',
      materialName: selectedMaterial?.name ?? '',
      unitText: '',
      scopeText: '',
    });
  };

  const footerTemplate = () => (
    <div className="flex justify-end gap-2 p-2">
      <ButtonComponent
        onClick={onClose}
        cssClass="e-outline e-btn e-outline"
        type="button"
      >
        {t('actions.cancel')}
      </ButtonComponent>
      <ButtonComponent
        onClick={handleOk}
        cssClass="e-primary e-btn e-primary"
        type="button"
      >
        {t('actions.ok')}
      </ButtonComponent>
    </div>
  );

  const dialogContent = () => (
    <div className="p-4">
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('routing.form.material', { defaultValue: 'Material' })} *
        </label>
        <DropDownListComponent
          dataSource={materials}
          fields={{ text: 'name', value: 'uuid' }}
          value={materialId || null}
          change={(e: any) => {
            setMaterialId(e.value || '');
            setErrors(prev => ({ ...prev, material: undefined }));
          }}
          allowFiltering={true}
          noRecordsTemplate={t('noRecords', { defaultValue: 'Keine Datensätze gefunden' })}
          locale="de-DE"
          placeholder={t('routing.form.material', { defaultValue: 'Material' })}
          cssClass={errors.material ? 'e-error' : ''}
        />
        {errors.material && (
          <div className="text-red-500 text-sm mt-1">{errors.material}</div>
        )}
      </div>
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('routing.form.quantity', { defaultValue: 'Menge' })} *
        </label>
        <NumericTextBoxComponent
          value={quantity}
          min={0}
          format="n3"
          locale="de-DE"
          change={(e: { value?: number | string | null }) => {
            const n = typeof e.value === 'number' ? e.value : Number(e.value ?? 0);
            setQuantity(Number.isNaN(n) ? 0 : n);
            setErrors(prev => ({ ...prev, quantity: undefined }));
          }}
          cssClass={errors.quantity ? 'e-error' : ''}
        />
        {errors.quantity && (
          <div className="text-red-500 text-sm mt-1">{errors.quantity}</div>
        )}
      </div>
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('routing.form.inputQuantityForm.scope', { defaultValue: 'Bedarfsskalierung' })} *
        </label>
        <DropDownListComponent
          dataSource={scopeOptions}
          fields={{ text: 'text', value: 'value' }}
          value={quantityScope}
          change={(e: any) => setQuantityScope((e.value as QuantityScope) ?? 'PER_PIECE')}
          locale="de-DE"
        />
      </div>
    </div>
  );

  return (
    <DialogComponent
      visible={true}
      isModal={true}
      header={isNew
        ? (t('routing.form.inputQuantityForm.add', { defaultValue: 'Eingangsmaterial hinzufügen' }) as string)
        : (t('routing.form.inputQuantityForm.edit', { defaultValue: 'Eingangsmaterial bearbeiten' }) as string)}
      showCloseIcon={true}
      closeOnEscape={true}
      width="500px"
      close={onClose}
      target="body"
      animationSettings={{ effect: 'None' }}
      cssClass="input-quantity-sub-dialog"
      zIndex={10010}
      footerTemplate={footerTemplate}
    >
      {dialogContent()}
    </DialogComponent>
  );
};

export default OperationDetailDialog;
